// PickUpDraftBoard.js
// The shared, presentational draft board for Pick Up Draft. Both the multiplayer screen
// (PickUpDraft.js, driven by a live Firestore game) and the solo screen (PickUpCpu.js,
// driven by a local in-memory game) render THIS component, so the layout lives in exactly
// one place. It is purely visual: it reads a decoded game object through the same pure
// helpers used everywhere else and calls back up when the user makes a pick.
//
// Props:
//   game      — decoded game object (from Firestore or built locally; same shape either way)
//   uid       — the current user's id (Player A in a CPU game)
//   onPick    — (position, slotInRound, player) => void, fired when the user taps a card
//   onBack    — () => void, the back button handler
//   showTimer — whether to show the per-pick countdown (true for multiplayer, false for solo)
//   toast     — optional error/notice string to show above the cards (parent-controlled)
//
// It owns only its own view state: which position tab is selected, whether the roster
// overlay is open, and the YOU/OPP chemistry toggle. Turn ownership, the pool, taken
// players, etc. all come from the game via the pickupService helpers.

import React, { useState, useEffect } from "react";
import PlayerCard from "./PlayerCard";
import ChemistryTracker from "./ChemistryTracker";
import RosterPanel from "./RosterPanel";
import { loadTeamLogos } from "../managers/teamLogoService";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import {
  isUserTurn,
  totalPicks,
  opponentID,
  opponentUsername,
  draftedPlayers,
  draftedPositions,
  currentRoundPositions,
  pool,
  takenPlayerNames,
  unfilledSlots,
  isSlotFilled,
} from "../managers/pickupService";

const APP_THEME = "#d4f53c";

const SPORTS = [
  { id: "football", label: "Football", hex: "#00b04f", icon: footballIcon },
  { id: "baseball", label: "Baseball", hex: "#db2626", icon: baseballIcon },
  { id: "basketball", label: "Basketball", hex: "#ff8c00", icon: basketballIcon },
  { id: "hockey", label: "Hockey", hex: "#0da6e8", icon: hockeyIcon },
  { id: "soccer", label: "Soccer", hex: "#1a66e6", icon: soccerIcon },
];
const sportMeta = (id) => SPORTS.find((s) => s.id === id) || SPORTS[0];

function fmtTimer(expiresAt) {
  if (!expiresAt) return "--:--";
  const s = Math.floor((expiresAt.getTime() - Date.now()) / 1000);
  if (s <= 0) return "00:00";
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  return `${hh}:${mm}`;
}
function isUrgent(expiresAt) {
  if (!expiresAt) return false;
  const ms = expiresAt.getTime() - Date.now();
  return ms > 0 && ms < 3600 * 1000;
}

export default function PickUpDraftBoard({ game, uid, onPick, onBack, showTimer = true, toast = "" }) {
  const [selectedSlot, setSelectedSlot] = useState(0);
  const [showRoster, setShowRoster] = useState(false);
  const [, setTick] = useState(0); // drives the timer re-render
  const [, bumpLogos] = useState(0); // forces a re-render once the logo URL map resolves

  // Warm the team-logo URL map so PlayerCard's logoURL() resolves (same as Draft / Knockout).
  // bundledLogo() works without this, but the Firestore-hosted logos need the map cached.
  useEffect(() => {
    loadTeamLogos().then(() => bumpLogos((n) => n + 1)).catch(() => {});
  }, []);

  // Timer ticker — only needed when a countdown is shown.
  useEffect(() => {
    if (!showTimer) return undefined;
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [showTimer]);

  const meta = sportMeta(game.sport);
  const yourTurn = uid ? isUserTurn(game, uid) : false;
  const oppID = uid ? opponentID(game, uid) : game.playerBID;
  const roundIdx = game.currentRoundIndex;
  const positions = currentRoundPositions(game);
  const unfilledIdx = uid
    ? new Set(unfilledSlots(game, uid, roundIdx).map((s) => s.slotInRound))
    : new Set();
  const taken = takenPlayerNames(game, roundIdx);
  const myPickNames = new Set(
    game.picks.filter((p) => p.userID === uid).map((p) => p.player.name)
  );
  const myPickCount = game.picks.filter((p) => p.userID === uid).length;

  const done = game.picks.length;
  const total = totalPicks(game);
  const selPosition = selectedSlot < positions.length ? positions[selectedSlot] : "—";

  const slotPlayers = selectedSlot < positions.length
    ? pool(game, roundIdx, positions[selectedSlot], selectedSlot)
    : [];
  const slotFilled = uid && selectedSlot < positions.length
    ? isSlotFilled(game, uid, roundIdx, selectedSlot)
    : false;

  return (
    <div style={styles.page}>
      {/* Header — back (left) + roster button (right). The shell already shows branding,
          so the centered wordmark is gone and the content sits higher. */}
      <div style={styles.header}>
        <button style={styles.iconBtn} onClick={onBack}>‹</button>
        <button style={styles.rosterBtn} onClick={() => setShowRoster(true)} title="View rosters">
          <span style={styles.rosterIcon}>☰</span>
          {myPickCount > 0 && (
            <span style={{ ...styles.rosterBadge, background: meta.hex }}>{myPickCount}</span>
          )}
        </button>
      </div>

      {/* Progress — sport-logo circle on the left, with the DRAFTING label and the bar
          stacked beside it and vertically centered against the circle. */}
      <div style={styles.progressRow}>
        <div style={{ ...styles.progressIcon, background: meta.hex }}>
          <span
            style={{
              ...styles.progressGlyph,
              WebkitMaskImage: `url(${meta.icon})`,
              maskImage: `url(${meta.icon})`,
            }}
          />
        </div>
        <div style={styles.progressBody}>
          <div style={styles.progressTopRow}>
            <div style={styles.progressLabel}>
              DRAFTING <span style={{ color: meta.hex }}>{selPosition}</span>
            </div>
            <div style={styles.progressCount}>{done} OF {total}</div>
          </div>
          <div style={styles.barTrack}>
            <div style={{ ...styles.barFill, width: `${(done / total) * 100}%`, background: meta.hex }} />
          </div>
        </div>
      </div>

      {/* Turn banner */}
      <div
        style={{
          ...styles.turnBanner,
          background: yourTurn ? `${APP_THEME}14` : "rgba(255,255,255,0.02)",
        }}
      >
        <span
          style={{
            ...styles.turnDot,
            background: yourTurn ? APP_THEME : "rgba(255,255,255,0.3)",
          }}
        />
        <span style={{ ...styles.turnText, color: yourTurn ? APP_THEME : "rgba(255,255,255,0.4)" }}>
          {yourTurn ? "YOUR PICK" : `${uid ? opponentUsername(game, uid) : ""}'S TURN`}
        </span>
        <span style={{ flex: 1 }} />
        {showTimer && (
          <span
            style={{
              ...styles.timer,
              color: isUrgent(game.turnExpiresAt) ? "#ff5a5a" : "rgba(255,255,255,0.85)",
              borderColor: isUrgent(game.turnExpiresAt) ? "rgba(255,90,90,0.3)" : "rgba(255,255,255,0.12)",
            }}
          >
            ⏱ {fmtTimer(game.turnExpiresAt)}
          </span>
        )}
      </div>

      {/* Position tabs */}
      <div style={styles.tabs}>
        {positions.map((position, i) => {
          const filled = !unfilledIdx.has(i);
          const selected = selectedSlot === i;
          return (
            <button
              key={`${position}_${i}`}
              onClick={() => setSelectedSlot(i)}
              style={{
                ...styles.tab,
                background: selected ? meta.hex : "#1a1d24",
                color: selected ? "#0c0d10" : filled ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.7)",
                borderColor: selected ? meta.hex : "rgba(255,255,255,0.08)",
              }}
            >
              {filled ? "✓ " : ""}{position}
            </button>
          );
        })}
      </div>

      {toast && <div style={styles.toast}>{toast}</div>}

      {/* Player cards for the selected slot */}
      <div style={styles.cards}>
        {slotPlayers.map((pl, i) => {
          const isTaken = taken.has(pl.name);
          const isMine = myPickNames.has(pl.name);
          const canPick = yourTurn && !slotFilled && !isTaken;
          return (
            <div
              key={`${pl.name}-${i}`}
              style={{
                ...styles.cardWrap,
                opacity: isTaken && !isMine ? 0.6 : 1,
                outline: isMine ? `1.5px solid ${APP_THEME}` : "none",
                borderRadius: 16,
              }}
            >
              <PlayerCard
                player={pl}
                sport={meta}
                position={positions[selectedSlot]}
                showRating={false}
                isDisabled={!canPick}
                onTap={() => {
                  if (canPick) onPick(positions[selectedSlot], selectedSlot, pl);
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Chemistry tracker (YOU / OPP) */}
      <ChemistryToggleTracker game={game} uid={uid} meta={meta} />

      {showRoster && (
        <RosterPanel
          sport={meta}
          onClose={() => setShowRoster(false)}
          teams={[
            {
              label: "YOU",
              players: draftedPlayers(game, uid),
              positions: draftedPositions(game, uid),
            },
            {
              label: ((uid ? opponentUsername(game, uid) : game.playerBUsername) || "OPP").toUpperCase(),
              players: draftedPlayers(game, oppID),
              positions: draftedPositions(game, oppID),
            },
          ]}
        />
      )}
    </div>
  );
}

// YOU/OPP toggle lives INSIDE the chemistry tracker box (top-right) via its headerRight
// slot, so it doesn't add any height or push the other elements around.
function ChemistryToggleTracker({ game, uid, meta }) {
  const [showOpp, setShowOpp] = useState(false);
  if (!uid) return null;
  const oppID = opponentID(game, uid);
  const players = showOpp ? draftedPlayers(game, oppID) : draftedPlayers(game, uid);

  const toggle = (
    <>
      <button
        onClick={() => setShowOpp(false)}
        style={{ ...styles.chemTab, ...(showOpp ? {} : { background: meta.hex, color: "#0c0d10", borderColor: meta.hex }) }}
      >
        YOU
      </button>
      <button
        onClick={() => setShowOpp(true)}
        style={{ ...styles.chemTab, ...(showOpp ? { background: meta.hex, color: "#0c0d10", borderColor: meta.hex } : {}) }}
      >
        OPP
      </button>
    </>
  );

  return <ChemistryTracker players={players} sport={meta} headerRight={toggle} />;
}

const styles = {
  page: { padding: "16px 16px 96px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "100vh" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  iconBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 28, cursor: "pointer", width: 32, lineHeight: 1, textAlign: "left" },
  rosterBtn: { background: "none", border: "none", position: "relative", width: 32, display: "flex", alignItems: "center", justifyContent: "flex-end", cursor: "pointer" },
  rosterIcon: { fontSize: 20, color: "rgba(255,255,255,0.8)" },
  rosterBadge: { position: "absolute", top: -6, right: -4, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#000" },
  progressRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 14 },
  progressIcon: { width: 40, height: 40, borderRadius: 20, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  progressGlyph: {
    width: 22,
    height: 22,
    display: "block",
    background: "#0c0d10",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
  },
  progressBody: { flex: 1, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  progressTopRow: { display: "flex", alignItems: "center", gap: 12 },
  progressLabel: { fontSize: 16, fontWeight: 600, color: "rgba(255,255,255,0.7)", letterSpacing: 0.5, flex: 1 },
  progressCount: { fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 1 },
  barTrack: { height: 8, background: "#1a1d24", borderRadius: 4 },
  barFill: { height: 8, borderRadius: 4, transition: "width 0.3s" },
  turnBanner: { display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 14px", borderRadius: 10, marginBottom: 12 },
  turnDot: { width: 9, height: 9, borderRadius: 5 },
  turnText: { fontSize: 13, fontWeight: 800, letterSpacing: 1.5 },
  timer: { fontFamily: "monospace", fontSize: 13, fontWeight: 700, padding: "4px 10px", borderRadius: 20, border: "1px solid", background: "rgba(255,255,255,0.06)" },
  tabs: { display: "flex", gap: 8, marginBottom: 12, justifyContent: "center" },
  tab: { flex: 1, maxWidth: 140, height: 40, borderRadius: 10, border: "1px solid", fontSize: 14, fontWeight: 800, letterSpacing: 1, cursor: "pointer" },
  toast: { background: "#2a1618", border: "1px solid #5a2a2e", color: "#ffb4b4", borderRadius: 10, padding: "8px 12px", marginBottom: 10, fontSize: 13 },
  cards: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 },
  cardWrap: {},
  dim: { fontSize: 14, color: "#6e747e", textAlign: "center", marginTop: 40 },
  chemTab: { padding: "2px 9px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(255,255,255,0.6)", fontSize: 10, fontWeight: 800, letterSpacing: 1, cursor: "pointer", lineHeight: 1.6 },
};
